/**
 * TeamSection — Mini-Project public team display on the About tab.
 *
 * POLICY_TEAM_ROSTERS.md §5.1 — uses FlipTeamMemberCard, the canonical
 * render for both Settings preview and detail-page roster. Lead-first
 * order, then sort_order, then joined_at. Hides members with
 * isHidden=true for non-Lead viewers (server already filters this on
 * the GET endpoint when the caller isn't an admin; client-side filter
 * is belt-and-braces).
 *
 * Listens for l4e_miniproject_team_persisted (fired by the in-memory
 * store after the Settings modal Save flow completes) and triggers a
 * refetch via the parent's onMutated callback so the About tab
 * refreshes without requiring a navigation. _changed events are NOT
 * consumed here — those fire on every keystroke in Settings and would
 * leak in-flight admin edits to the public view.
 *
 * Display-name and avatar resolution comes from useApiSecurityData,
 * same pattern as the Settings Team tab — so the rows render as real
 * names rather than truncated GUIDs.
 *
 * NOTE: Strings hardcoded pending Mini-Project i18n sweep (DEV-1062),
 * in line with the rest of the detail page.
 */

import { useEffect, useMemo } from "react";
import FlipTeamMemberCard from "../../../Components/TeamMemberCard/FlipTeamMemberCard";
import { useApiSecurityData } from "../../../api/useApiSecurityData";
import type { MiniProjectTeamMember } from "../../../api/miniProjects";
import styles from "./sections.module.css";

interface Props {
  miniProjectGuid: string;
  teamMembers: MiniProjectTeamMember[];
  isLead: boolean;
  currentUserGuid: string;
  /** Fired by the parent (AboutTabPanel) when this section should
   *  refresh its team-member list — e.g., after the Settings modal
   *  Save commits via the bulk endpoint. */
  onMutated: () => void;
}

interface L4eUser {
  userGuid: string;
  displayName: string | null;
  name: string | null;
  email: string | null;
  screenName: string | null;
  profileImageUrl: string | null;
}

export default function TeamSection({
  miniProjectGuid,
  teamMembers,
  isLead,
  currentUserGuid,
  onMutated,
}: Props) {
  // Listen for the Settings-modal save event. _persisted fires only
  // after a successful bulk-save round-trip, NOT on every keystroke,
  // so the About tab refreshes when the Lead saves but stays stable
  // while they're mid-edit.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { miniProjectGuid?: string } | undefined;
      if (!detail?.miniProjectGuid) return;
      if (detail.miniProjectGuid.toUpperCase() !== miniProjectGuid.toUpperCase()) return;
      onMutated();
    };
    window.addEventListener("l4e_miniproject_team_persisted", handler);
    return () => window.removeEventListener("l4e_miniproject_team_persisted", handler);
  }, [miniProjectGuid, onMutated]);

  // Resolve userGuid → user record from the platform's security
  // snapshot. DEV-932: securityUsers is async; the memo below depends
  // on it so it recomputes when the data lands.
  const { securityUsers } = useApiSecurityData();
  const userByGuid = useMemo(() => {
    const m = new Map<string, L4eUser>();
    for (const u of (securityUsers || []) as any[]) {
      const guid = String(u.user_guid || u.userGuid || "");
      if (!guid) continue;
      m.set(guid.toUpperCase(), {
        userGuid: guid,
        displayName: u.display_name ?? u.displayName ?? null,
        name: u.name ?? null,
        email: u.email ?? null,
        screenName: u.screen_name ?? u.screenName ?? null,
        profileImageUrl: u.profile_image_url ?? u.profileImageUrl ?? null,
      });
    }
    return m;
  }, [securityUsers]);

  // Sort + filter. Lead-first, then sort_order, then joined_at.
  // Hidden filter: DEV-1090 follow-up — full hide. The About-tab
  // Teams display is the public-facing roster; hidden members are
  // omitted for every viewer, including the Lead. The Settings →
  // Team tab still shows the full roster (with a "Hidden" badge on
  // hidden rows) — that's where the Lead manages visibility. This
  // diverges from the prior "Lead sees, public doesn't" semantic
  // per Skip's 2026-05-24 decision: a hidden card on About would
  // be confusing for the Lead even with a badge, and the Settings
  // surface is the canonical place to see who's actually on the
  // team regardless of public visibility.
  const visible = useMemo(() => {
    const active = teamMembers.filter((m) => !m.leftAt);
    const filtered = active.filter((m) => !m.isHidden);
    return filtered.sort((a, b) => {
      if (a.role === "lead" && b.role !== "lead") return -1;
      if (b.role === "lead" && a.role !== "lead") return 1;
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.joinedAt.localeCompare(b.joinedAt);
    });
  }, [teamMembers]);

  if (visible.length === 0) {
    return <p className={styles.rowMeta}>No team members yet.</p>;
  }

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 16,
        alignItems: "flex-start",
      }}
    >
      {visible.map((m) => {
        const user = userByGuid.get(m.userGuid.toUpperCase());
        const titleLabel = (m.displayTitle || "").trim()
          || (m.role === "lead" ? "Lead" : "Member");
        const displayName = user?.displayName
          || user?.name
          || user?.screenName
          || `${m.userGuid.slice(0, 8)}…`;

        return (
          <FlipTeamMemberCard
            key={m.teamMemberGuid}
            displayName={displayName}
            photoUrl={user?.profileImageUrl || undefined}
            roleTitle={titleLabel}
            linkedUserGuid={m.userGuid}
            userGuid={m.userGuid}
            contactEmail={user?.email || undefined}
            backFaceBadge={m.role === "lead" ? "Lead" : "Member"}
            avatarVariant="l4e"
            splitName
            size="md"
          />
        );
      })}
    </div>
  );
}
